import React, { useMemo, useState } from 'react';
import { SunIcon, MoonIcon, AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { MOCK_LOANS, LOAN_STAGES, AlertType, ALERT_TYPES, LoanStage } from '../data/mockData';
import { PipelineFilters } from './PipelineFilters';
import { StageSection } from './StageSection';
export const LoanPipeline: React.FC = () => {
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedAlerts, setSelectedAlerts] = useState<AlertType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [darkMode, setDarkMode] = useState<boolean>(false);
  // Initialize all stages as expanded
  const [expandedStages, setExpandedStages] = useState<Record<LoanStage, boolean>>(() => {
    const initial: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      initial[stage] = true;
    });
    return initial as Record<LoanStage, boolean>;
  });
  const handleClearFilters = () => {
    setSelectedDivision('');
    setSelectedTeam('');
    setSelectedAlerts([]);
    setSearchQuery('');
  };
  const toggleStage = (stage: LoanStage) => {
    setExpandedStages(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };
  const expandAll = () => {
    const allExpanded: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allExpanded[stage] = true;
    });
    setExpandedStages(allExpanded as Record<LoanStage, boolean>);
  };
  const collapseAll = () => {
    const allCollapsed: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allCollapsed[stage] = false;
    });
    setExpandedStages(allCollapsed as Record<LoanStage, boolean>);
  };
  // Loans filtered by everything EXCEPT alerts (for KPI counts)
  const loansBeforeAlertFilter = useMemo(() => {
    return MOCK_LOANS.filter(loan => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = loan.borrowerName.toLowerCase().includes(q) || loan.loanNumber.toLowerCase().includes(q) || loan.loanOfficer.toLowerCase().includes(q) || loan.propertyAddress.toLowerCase().includes(q) || loan.teamLead.toLowerCase().includes(q) || loan.divisionLead.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      if (selectedDivision && loan.divisionLead !== selectedDivision) return false;
      if (selectedTeam && loan.teamLead !== selectedTeam) return false;
      return true;
    });
  }, [selectedDivision, selectedTeam, searchQuery]);
  const filteredLoans = useMemo(() => {
    return loansBeforeAlertFilter.filter(loan => {
      if (selectedAlerts.length > 0) {
        const hasMatchingAlert = loan.alerts.some(alert => selectedAlerts.includes(alert));
        if (!hasMatchingAlert) return false;
      }
      return true;
    });
  }, [loansBeforeAlertFilter, selectedAlerts]);
  // Compute alert counts from loansBeforeAlertFilter
  const alertCounts = useMemo(() => {
    const counts: Record<AlertType, number> = {} as Record<AlertType, number>;
    ALERT_TYPES.forEach(alert => {
      counts[alert] = loansBeforeAlertFilter.filter(loan => loan.alerts.includes(alert)).length;
    });
    return counts;
  }, [loansBeforeAlertFilter]);
  const totalPipelineValue = useMemo(() => {
    return filteredLoans.reduce((sum, loan) => sum + loan.amount, 0);
  }, [filteredLoans]);
  const formattedPipelineValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(totalPipelineValue);
  const getAlertCardStyle = (alert: AlertType) => {
    switch (alert) {
      case 'Rate Lock Expiring':
      case 'Missing Documents':
      case 'Closing Date Approaching':
        return {
          color: 'red',
          icon: <AlertCircleIcon className="w-4 h-4" data-id="element-24" />
        };
      case 'AWC No Appraisal Back':
      case 'Disclosures Out Not Signed':
      case 'Disclosures Signed Not Submitted':
        return {
          color: 'orange',
          icon: <AlertTriangleIcon className="w-4 h-4" data-id="element-25" />
        };
      case 'AWC Appraisal Back':
        return {
          color: 'green',
          icon: <CheckCircleIcon className="w-4 h-4" data-id="element-26" />
        };
      default:
        return {
          color: 'gray',
          icon: null
        };
    }
  };
  const allExpanded = Object.values(expandedStages).every(Boolean);
  return <div className={`flex flex-col h-screen overflow-hidden font-sans ${darkMode ? 'dark bg-gray-900' : 'bg-gray-100'}`} data-id="element-27">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-none transition-colors duration-200" data-id="element-28">
        <div className="flex items-center gap-6" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/mrGufM5MFoDnTo49ELcw9x/2025-10-31_EZLoans_LogoAssets-HorizontalBlack_resized.png" alt="EZ Loans" className="h-8 object-contain block dark:hidden" data-id="element-30" />
          <img src="https://cdn.magicpatterns.com/uploads/jRVMd8MT9eXArUDKWYuZaB/2025-10-31_EZLoans_LogoAssets-HorizontalWhite_resized.png" alt="EZ Loans" className="h-8 object-contain hidden dark:block" data-id="element-31" />
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 hidden sm:block transition-colors duration-200" data-id="element-32"></div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100 hidden sm:block transition-colors duration-200" data-id="element-33">
            Loan Pipeline
          </h1>
        </div>
        <div className="flex items-center gap-4" data-id="element-34">
          {/* Pipeline Value + Loan Count */}
          <div className="hidden sm:flex items-center gap-3" data-id="element-35">
            <span className="text-lg font-bold text-[#6DB33F]" data-id="element-36">
              {formattedPipelineValue}
            </span>
            <div className="text-sm text-gray-600 dark:text-gray-300 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors duration-200" data-id="element-37">
              {filteredLoans.length} Loan{filteredLoans.length !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 hidden sm:block" data-id="element-38"></div>

          {/* Dark Mode Toggle */}
          <button onClick={() => setDarkMode(!darkMode)} className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} aria-label="Toggle dark mode" data-id="element-39">
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-sm transition-transform duration-300 flex items-center justify-center ${darkMode ? 'translate-x-9' : 'translate-x-1'}`} data-id="element-40">
              {darkMode ? <MoonIcon className="h-4 w-4 text-[#6DB33F]" data-id="element-41" /> : <SunIcon className="h-4 w-4 text-[#6DB33F]" data-id="element-42" />}
            </span>
          </button>

          <div className="w-8 h-8 rounded-full bg-[#6DB33F] text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-43">
            JD
          </div>
        </div>
      </header>

      {/* Filters */}
      <PipelineFilters selectedDivision={selectedDivision} setSelectedDivision={setSelectedDivision} selectedTeam={selectedTeam} setSelectedTeam={setSelectedTeam} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onClearFilters={handleClearFilters} data-id="element-44" />

      {/* Pipeline Board (List View) */}
      <main className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-45">
        <div className="w-full" data-id="element-46">
          {/* Alert KPI Cards */}
          <div className="flex items-center justify-between mb-4" data-id="element-47">
            <div className="flex flex-wrap gap-3" data-id="element-48">
              {ALERT_TYPES.map(alert => {
              const isActive = selectedAlerts.includes(alert);
              const {
                color,
                icon
              } = getAlertCardStyle(alert);
              const count = alertCounts[alert];
              const colorClasses = {
                red: {
                  active: 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-red-300 dark:hover:border-red-700',
                  icon: 'text-red-500 dark:text-red-400',
                  count: 'text-red-600 dark:text-red-400'
                },
                orange: {
                  active: 'border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-orange-300 dark:hover:border-orange-700',
                  icon: 'text-orange-500 dark:text-orange-400',
                  count: 'text-orange-600 dark:text-orange-400'
                },
                green: {
                  active: 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-700',
                  icon: 'text-green-500 dark:text-green-400',
                  count: 'text-green-600 dark:text-green-400'
                },
                gray: {
                  active: 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-700',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300',
                  icon: 'text-gray-500 dark:text-gray-400',
                  count: 'text-gray-600 dark:text-gray-400'
                }
              };
              const classes = colorClasses[color as keyof typeof colorClasses];
              return <button key={alert} onClick={() => {
                if (isActive) {
                  setSelectedAlerts(selectedAlerts.filter(a => a !== alert));
                } else {
                  setSelectedAlerts([...selectedAlerts, alert]);
                }
              }} className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border-2 shadow-sm transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6DB33F] ${isActive ? classes.active : classes.inactive}`} data-id="element-49">
                    <span className={classes.icon} data-id="element-50">{icon}</span>
                    <div className="flex flex-col items-start" data-id="element-51">
                      <span className={`text-xl font-bold leading-none ${classes.count}`} data-id="element-52">
                        {count}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5 whitespace-nowrap" data-id="element-53">
                        {alert}
                      </span>
                    </div>
                  </button>;
            })}
            </div>

            <button onClick={allExpanded ? collapseAll : expandAll} className="text-sm font-medium text-[#6DB33F] hover:text-green-700 transition-colors whitespace-nowrap ml-4" data-id="element-54">
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Active alert filter indicator */}
          {selectedAlerts.length > 0 && <div className="flex items-center gap-2 mb-3" data-id="element-55">
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-56">
                Filtering by {selectedAlerts.length} alert
                {selectedAlerts.length > 1 ? 's' : ''}
              </span>
              <button onClick={() => setSelectedAlerts([])} className="text-xs text-[#6DB33F] hover:text-green-700 font-medium transition-colors" data-id="element-57">
                Clear alert filters
              </button>
            </div>}

          {/* Stage Sections */}
          <div className="flex flex-col gap-2" data-id="element-58">
            {LOAN_STAGES.map(stage => {
            const stageLoans = filteredLoans.filter(loan => loan.stage === stage);
            return <StageSection key={stage} stage={stage} loans={stageLoans} isExpanded={expandedStages[stage]} onToggle={() => toggleStage(stage)} data-id="element-59" />;
          })}
          </div>
        </div>
      </main>
    </div>;
};